import {
  fetchBaseQuery,
  type BaseQueryApi,
  type FetchArgs,
  type FetchBaseQueryError
} from '@reduxjs/toolkit/query';
import { Mutex } from 'async-mutex';
import { toast } from 'react-toastify';
import {
  getCookie,
  setCookie,
  removeCookie
} from '@/app/common/utils/cookieService';
import { getBaseApiPath } from '@/app/common/api/config';
import { logTokenRefreshFailure } from '@/app/common/utils/logTokenRefreshFailure';

const mutex = new Mutex();
const pathBlacklist = ['getPostBySlug'];
const REFRESH_LOCK_NAME = 'mizfa-token-refresh';
const REFRESH_STORAGE_LOCK_KEY = 'mizfa_token_refresh_lock';
const AUTH_CHANNEL_NAME = 'mizfa-tools-auth';

type AuthSyncMessage =
  | {
      type: 'tokens';
      accessToken: string;
      refreshToken?: string | null;
    }
  | { type: 'logout' };

type AuthStateSlice = {
  authApi?: {
    queries?: Record<
      string,
      { data?: { data?: { path?: string } } } | undefined
    >;
  };
};

type ApiErrorData = {
  code?: number;
  errorMessage?: string;
  status?: number;
  handled?: boolean;
  [key: string]: unknown;
};

type RefreshTokenResponse = {
  success?: boolean;
  data?: {
    access_token?: string;
    refresh_token?: string;
  };
};

type TokenRefreshLogContext = {
  failedApiUrl?: string;
};

const buildRequestUrl = (
  baseUrl: string,
  args: string | FetchArgs
): string => {
  if (typeof args === 'string') {
    const normalizedBase = baseUrl.replace(/\/$/, '');
    const normalizedPath = args.replace(/^\//, '');
    return `${normalizedBase}/${normalizedPath}`;
  }

  const path = args.url ?? '';
  if (path.startsWith('http')) {
    return path;
  }

  const normalizedBase = baseUrl.replace(/\/$/, '');
  const normalizedPath = path.replace(/^\//, '');
  return `${normalizedBase}/${normalizedPath}`;
};

const isPublicReportingPath = (): boolean => {
  if (typeof window === 'undefined') return false;
  const path = window.location.pathname.replace(/\/+$/, '');
  return (
    /^\/reporting\/page_speed\/[^/]+$/.test(path) ||
    /^\/reporting\/tracker\/[^/]+$/.test(path)
  );
};

const isPublicApiRequest = (args: string | FetchArgs): boolean => {
  const url = typeof args === 'string' ? args : args.url;
  return typeof url === 'string' && url.includes('/lighthouse/v1/public/');
};

const shouldSkipLogoutOn401 = (
  api: BaseQueryApi,
  args: string | FetchArgs
): boolean => {
  if (isPublicReportingPath() || isPublicApiRequest(args)) {
    return true;
  }

  const { endpoint } = api;
  const params =
    typeof args === 'object' && args && 'params' in args
      ? ((args.params as Record<string, unknown> | undefined) ?? {})
      : {};

  if (endpoint === 'getNotifs') {
    try {
      const rawFilter = params.filter;
      const filter =
        typeof rawFilter === 'string' ? JSON.parse(rawFilter) : rawFilter;
      const type =
        filter && typeof filter === 'object' && 'type' in filter
          ? (filter as { type?: string }).type
          : undefined;
      return type === 'pop_up' || type === 'top';
    } catch {
      return false;
    }
  }

  return false;
};

let accessTokenMemory: string | null | undefined = getCookie(
  import.meta.env.VITE_ACCESS_TOKEN_NAME
);
let refreshTokenMemory: string | null | undefined = getCookie(
  import.meta.env.VITE_REFRESH_TOKEN_NAME
);

const authChannel = openAuthChannel();

function readAccessCookie(): string | null {
  return getCookie(import.meta.env.VITE_ACCESS_TOKEN_NAME) || null;
}

function readRefreshCookie(): string | null {
  return getCookie(import.meta.env.VITE_REFRESH_TOKEN_NAME) || null;
}

function rememberTokens(
  accessToken: string,
  refreshToken?: string | null
): void {
  accessTokenMemory = accessToken;
  if (refreshToken) refreshTokenMemory = refreshToken;
}

function openAuthChannel(): BroadcastChannel | null {
  if (typeof BroadcastChannel === 'undefined') return null;
  const channel = new BroadcastChannel(AUTH_CHANNEL_NAME);
  channel.onmessage = (event: MessageEvent<AuthSyncMessage>) => {
    const message = event.data;
    if (!message || typeof message !== 'object') return;
    if (message.type === 'tokens' && message.accessToken) {
      rememberTokens(message.accessToken, message.refreshToken);
      return;
    }
    if (message.type === 'logout') {
      accessTokenMemory = null;
      refreshTokenMemory = null;
      if (!window.location.pathname.startsWith('/login')) {
        window.location.replace('/login');
      }
    }
  };
  return channel;
}

function publishTokens(accessToken: string, refreshToken?: string | null): void {
  rememberTokens(accessToken, refreshToken);
  setCookie(import.meta.env.VITE_ACCESS_TOKEN_NAME, accessToken, 1);
  if (refreshToken) {
    setCookie(import.meta.env.VITE_REFRESH_TOKEN_NAME, refreshToken, 7);
  }
  authChannel?.postMessage({
    type: 'tokens',
    accessToken,
    refreshToken: refreshToken ?? null
  } satisfies AuthSyncMessage);
}

/**
 * Cookie is shared across tabs. Another tab may already have rotated the
 * pair while this tab waited, so reuse that pair instead of refreshing again.
 */
function adoptRotatedTokens(accessAtStart: string | null): boolean {
  const cookieAccess = readAccessCookie();
  if (!cookieAccess || cookieAccess === accessAtStart) return false;
  rememberTokens(cookieAccess, readRefreshCookie());
  return true;
}

async function withCrossTabRefreshLock<T>(
  task: () => Promise<T>
): Promise<T> {
  if (typeof navigator !== 'undefined' && navigator.locks?.request) {
    return navigator.locks.request(REFRESH_LOCK_NAME, task);
  }
  return withStorageRefreshLock(task);
}

function withStorageRefreshLock<T>(task: () => Promise<T>): Promise<T> {
  const owner = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  const started = Date.now();

  const tryAcquire = (): boolean => {
    const now = Date.now();
    try {
      const raw = localStorage.getItem(REFRESH_STORAGE_LOCK_KEY);
      const current = raw
        ? (JSON.parse(raw) as { owner?: string; until?: number })
        : null;
      if (
        current?.owner &&
        current.owner !== owner &&
        (current.until ?? 0) > now
      ) {
        return false;
      }
      localStorage.setItem(
        REFRESH_STORAGE_LOCK_KEY,
        JSON.stringify({ owner, until: now + 15000 })
      );
      const confirmed = JSON.parse(
        localStorage.getItem(REFRESH_STORAGE_LOCK_KEY) || 'null'
      ) as { owner?: string } | null;
      return confirmed?.owner === owner;
    } catch {
      return true;
    }
  };

  const release = (): void => {
    try {
      const raw = localStorage.getItem(REFRESH_STORAGE_LOCK_KEY);
      const current = raw
        ? (JSON.parse(raw) as { owner?: string })
        : null;
      if (current?.owner === owner) {
        localStorage.removeItem(REFRESH_STORAGE_LOCK_KEY);
      }
    } catch {
      /* ignore */
    }
  };

  return new Promise((resolve, reject) => {
    const attempt = (): void => {
      if (!tryAcquire()) {
        if (Date.now() - started > 20000) {
          task().then(resolve, reject);
          return;
        }
        window.setTimeout(attempt, 40 + Math.floor(Math.random() * 60));
        return;
      }
      task().then(resolve, reject).finally(release);
    };
    attempt();
  });
}

async function refreshLocked(
  logContext: TokenRefreshLogContext | undefined,
  accessAtStart: string | null
): Promise<boolean> {
  if (adoptRotatedTokens(accessAtStart)) return true;

  const accessToken = readAccessCookie() || accessTokenMemory || null;
  let refreshToken: string | null | undefined;
  let refreshResponse: Response | undefined;
  let refreshJson: RefreshTokenResponse | null = null;

  try {
    refreshToken = readRefreshCookie() || refreshTokenMemory;
    if (!refreshToken) {
      throw new Error('Refresh token missing');
    }

    refreshResponse = await fetch(
      `${getBaseApiPath()}/api/mizfa_tools/refresh_token`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken })
      }
    );

    refreshJson = (await refreshResponse.json().catch(() => null)) as
      | RefreshTokenResponse
      | null;
    if (
      !refreshResponse.ok ||
      !refreshJson?.success ||
      !refreshJson?.data?.access_token
    ) {
      if (adoptRotatedTokens(accessAtStart)) return true;
      await logTokenRefreshFailure({
        accessToken,
        refreshToken,
        failedApiUrl: logContext?.failedApiUrl,
        refreshStatusCode: refreshResponse.status,
        refreshResponseBody: refreshJson
          ? JSON.stringify(refreshJson)
          : null,
        reason: 'refresh_failed'
      });
      void clearTokensAndLogout();
      return false;
    }

    publishTokens(
      refreshJson.data.access_token,
      refreshJson.data.refresh_token
    );
    return true;
  } catch (e) {
    if (adoptRotatedTokens(accessAtStart)) return true;
    console.error('Token refresh error:', e);
    await logTokenRefreshFailure({
      accessToken,
      refreshToken,
      failedApiUrl: logContext?.failedApiUrl,
      refreshStatusCode: refreshResponse?.status,
      refreshResponseBody: refreshJson ? JSON.stringify(refreshJson) : null,
      errorMessage: e instanceof Error ? e.message : String(e),
      reason: 'refresh_error'
    });
    void clearTokensAndLogout();
    return false;
  }
}

export function getAccessToken(): string | null {
  return readAccessCookie() || accessTokenMemory || null;
}

/** Refresh access token via main API. Shared by RTK Query and MCP raw fetch. */
export async function refreshAccessToken(
  logContext?: TokenRefreshLogContext
): Promise<boolean> {
  const accessAtStart = readAccessCookie() || accessTokenMemory || null;
  const release = await mutex.acquire();
  try {
    return await withCrossTabRefreshLock(() =>
      refreshLocked(logContext, accessAtStart)
    );
  } finally {
    release();
  }
}

const getErrorData = (
  error: FetchBaseQueryError | undefined
): ApiErrorData | undefined => {
  if (!error || typeof error !== 'object' || !('data' in error)) {
    return undefined;
  }
  const data = error.data;
  if (data && typeof data === 'object') {
    return data as ApiErrorData;
  }
  return undefined;
};

const customFetchBase = async (
  url: string,
  args: string | FetchArgs,
  api: BaseQueryApi,
  extraOptions: Record<string, unknown> = {}
) => {
  const baseQuery = fetchBaseQuery({
    baseUrl: url,
    prepareHeaders: headers => {
      const state = api.getState() as AuthStateSlice;
      const userInfo = state.authApi?.queries?.['getUserInfo(undefined)']?.data;
      const userPath = userInfo?.data?.path;

      const token = getAccessToken();
      if (token && !headers.has('Authorization')) {
        headers.set('Authorization', `Bearer ${token}`);
      }

      if (userPath && !pathBlacklist.includes(api.endpoint)) {
        headers.set('Path', userPath);
      }

      return headers;
    }
  });

  await mutex.waitForUnlock();

  let result = await baseQuery(args, api, extraOptions);

  if (result?.error?.status === 401) {
    if (shouldSkipLogoutOn401(api, args)) {
      return result;
    }

    const errorData = getErrorData(result.error);
    if (errorData?.code === 40102) {
      const refreshed = await refreshAccessToken({
        failedApiUrl: buildRequestUrl(url, args)
      });
      if (refreshed) {
        result = await baseQuery(args, api, extraOptions);
      }
    } else {
      void clearTokensAndLogout();
    }
  }

  const resultErrorData = getErrorData(result.error);
  if (result?.error?.status === 500 && resultErrorData?.code === 0) {
    const errorMessage =
      'پردازش درخواست به‌دلیل ترافیک بالا یا ارسال درخواست‌های متوالی در سرورهای Google با تأخیر مواجه شده است.\nلطفا کمی بعد دوباره تلاش کنید.';
    toast.error(errorMessage);

    result = {
      ...result,
      error: {
        ...result.error,
        data: {
          ...resultErrorData,
          errorMessage,
          code: 0,
          status: 500,
          handled: true
        }
      }
    };
  }

  return result;
};

async function clearTokensAndLogout(): Promise<void> {
  accessTokenMemory = null;
  refreshTokenMemory = null;
  removeCookie(import.meta.env.VITE_ACCESS_TOKEN_NAME, true);
  removeCookie(import.meta.env.VITE_REFRESH_TOKEN_NAME, true);
  authChannel?.postMessage({ type: 'logout' } satisfies AuthSyncMessage);
  const { clearPersistedSelectedDomain } = await import(
    '@/app/common/utils/clearPersistedSelectedDomain'
  );
  await clearPersistedSelectedDomain();
  window.location.replace('/login');
}

export default customFetchBase;
